/**
 * Display and omission logic for the completed-instance record, shared by the
 * completed-task view and the definition history page. Kept free of React so
 * the rules can be unit-tested under the node-env vitest config.
 *
 * Formatters return null rather than a placeholder: NAU-366 requires fields
 * with no value to be omitted entirely, so the caller guards on null and the
 * label never renders.
 */

import { format } from "date-fns";

import {
  type ComplianceTaskDefinitionResponseDto,
  type ComplianceTaskFileDto,
  type ComplianceTaskInstanceResponseDto,
} from "@/generated/model";

import { parseExternalServiceFields } from "../external-service-fields";

export interface ServiceDetailRow {
  key: string;
  label: string;
  value: string;
}

export function formatRecordDate(value?: string | null): string | null {
  const date = toDate(value);
  if (!date) return null;
  return format(date, "d MMM yyyy");
}

export function formatRecordDateTime(value?: string | null): string | null {
  const date = toDate(value);
  if (!date) return null;
  return format(date, "d MMM yyyy 'at' HH:mm");
}

export function formatCompleterName(
  completer?: ComplianceTaskInstanceResponseDto["completedBy"],
): string | null {
  if (!completer) return null;
  const name = [completer.firstName, completer.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || completer.email || null;
}

export function formatFileSize(bytes?: number): string | null {
  if (!bytes || bytes <= 0) return null;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * Files are only linkable once the upload has finalised — the DTO types
 * `downloadUrl` as required, but the server omits it while the upload is
 * pending, which would otherwise render a broken anchor.
 */
export function downloadableFiles(
  instance: ComplianceTaskInstanceResponseDto,
): ComplianceTaskFileDto[] {
  return instance.files?.filter((file) => !!file.downloadUrl) ?? [];
}

export function formatServiceDetailValue(type: string, value: unknown): string {
  if (type === "yes_no") {
    return value === "yes" || value === true ? "Yes" : "No";
  }
  if (type === "date") {
    return formatRecordDate(String(value)) ?? String(value);
  }
  return String(value);
}

/**
 * Service detail answers in the definition's field order, labelled as the admin
 * configured them. Answers whose field is no longer in the config are appended
 * with a prettified key rather than dropped — a renamed field must not silently
 * remove part of the audit record.
 */
export function serviceDetailRows(
  instance: ComplianceTaskInstanceResponseDto,
  definition: ComplianceTaskDefinitionResponseDto,
): ServiceDetailRow[] {
  const data = instance.externalServiceData as
    | Record<string, unknown>
    | undefined;
  if (!data) return [];

  const fields = parseExternalServiceFields(definition.externalServiceConfig);
  const configured = new Set(fields.map((field) => field.name));

  const rows = fields
    .filter((field) => !isEmptyValue(data[field.name]))
    .map((field) => ({
      key: field.name,
      label: field.label,
      value: formatServiceDetailValue(field.type, data[field.name]),
    }));

  const orphaned = Object.keys(data)
    .filter((key) => !configured.has(key))
    .filter((key) => !isEmptyValue(data[key]))
    .map((key) => ({
      key,
      label: prettifyKey(key),
      value: formatServiceDetailValue("text", data[key]),
    }));

  return [...rows, ...orphaned];
}

export function hasCompletionNotes(
  instance: ComplianceTaskInstanceResponseDto,
): boolean {
  return !!instance.completionNotes?.trim();
}

export function hasChecklistResults(
  instance: ComplianceTaskInstanceResponseDto,
): boolean {
  return !!instance.checklistResults && instance.checklistResults.length > 0;
}

/** Whether any detail section has content. */
export function hasCompletionRecordDetail(
  instance: ComplianceTaskInstanceResponseDto,
  definition: ComplianceTaskDefinitionResponseDto,
): boolean {
  if (hasCompletionNotes(instance)) return true;
  if (hasChecklistResults(instance)) return true;
  if (downloadableFiles(instance).length > 0) return true;
  return serviceDetailRows(instance, definition).length > 0;
}

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  return typeof value === "string" && value.trim() === "";
}

function prettifyKey(key: string): string {
  const spaced = key
    .replace(/[_-]+/g, " ")
    .replace(/([A-Z])/g, " $1")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function toDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}
